import { createHash } from "node:crypto";
import { execFile } from "node:child_process";
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import {
  companionTargetFor,
  releaseAssetUrl,
  type CompanionComponent,
  type CompanionTarget,
} from "@m8t-stack/platform-release";
import { LocalCliError } from "./errors.js";

/** Larger than any build published so far, and bounded so a wrong URL cannot fill a disk. */
const MAX_ASSET_BYTES = 512 * 1024 * 1024;

export interface DownloadedCompanion {
  readonly version: string;
  /** Where the artifact manifest landed — what installCompanion is pointed at. */
  readonly artifactManifestPath: string;
  /** Removes the unpacked copy. Safe to call more than once. */
  readonly dispose: () => Promise<void>;
}

interface DownloadDeps {
  readonly fetchImpl?: typeof fetch;
  readonly makeTemporaryDirectory?: () => Promise<string>;
  readonly extract?: (archive: string, into: string) => Promise<void>;
  /** The host to fetch for. Named rather than read, so a test pins it. */
  readonly platform?: NodeJS.Platform;
  readonly architecture?: string;
}

function extractArchive(archive: string, into: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // The bytes were held to their pinned digest before this ran, so what is
    // being unpacked is the release's own build and nothing else.
    execFile("tar", ["-xzf", archive, "-C", into], { windowsHide: true }, (error) => {
      if (error) reject(new Error(`Could not unpack the companion build: ${error.message}`));
      else resolve();
    });
  });
}

/**
 * The build for this machine, or a reason there is none.
 *
 * Kept separate from the download so `status` can say what is available
 * without fetching several hundred megabytes to find out.
 */
export function companionAssetFor(
  component: CompanionComponent,
  platform: NodeJS.Platform = process.platform,
  architecture: string = process.arch,
): { target: CompanionTarget; asset: string; sha256: string } | null {
  const target = companionTargetFor(platform, architecture);
  if (target === null) return null;
  const pinned = component.targets[target];
  return pinned ? { target, asset: pinned.asset, sha256: pinned.sha256 } : null;
}

/**
 * Fetch the companion build the release pins for this machine and unpack it.
 *
 * The asset's own bytes are held to the digest in the manifest before anything
 * is unpacked from it, so a substituted archive never reaches tar. What comes
 * out is then verified again, file by file, by the install itself.
 */
export async function downloadCompanionArtifact(
  component: CompanionComponent,
  deps: DownloadDeps = {},
): Promise<DownloadedCompanion> {
  const platform = deps.platform ?? process.platform;
  const architecture = deps.architecture ?? process.arch;
  const pinned = companionAssetFor(component, platform, architecture);
  if (pinned === null) {
    throw new LocalCliError({
      code: "COMPANION_NO_BUILD_FOR_HOST",
      message: `Release ${component.version} carries no desktop companion for ${platform}-${architecture}.`,
      hint: "The companions are built for macOS and Windows on arm64 and x64.",
    });
  }
  const url = releaseAssetUrl(component.tag, pinned.asset);
  const fetchImpl = deps.fetchImpl ?? fetch;
  const response = await fetchImpl(url);
  if (!response.ok) {
    throw new LocalCliError({
      code: "COMPANION_ASSET_FETCH_FAILED",
      message: `GET ${url} returned HTTP ${response.status.toString()}.`,
      hint: "The release the platform is pinned to should carry this file. Try again, or run 'm8t companion repair' once the network is back.",
    });
  }
  const bytes = Buffer.from(await response.arrayBuffer());
  if (bytes.byteLength > MAX_ASSET_BYTES) {
    throw new LocalCliError({
      code: "COMPANION_ASSET_TOO_LARGE",
      message: `${pinned.asset} is larger than this command will accept.`,
    });
  }
  const digest = createHash("sha256").update(bytes).digest("hex");
  if (digest !== pinned.sha256) {
    throw new LocalCliError({
      code: "COMPANION_ASSET_DIGEST_MISMATCH",
      message: `${pinned.asset} does not match the digest the release pins for it.`,
      hint: "Nothing was unpacked. This is what a corrupted download or a substituted file looks like — retry, and report it if it persists.",
    });
  }

  const root = await (deps.makeTemporaryDirectory ??
    (() => fs.mkdtemp(path.join(os.tmpdir(), "m8t-companion-"))))();
  const dispose = async (): Promise<void> => {
    await fs.rm(root, { recursive: true, force: true }).catch(() => undefined);
  };
  try {
    const archive = path.join(root, pinned.asset);
    await fs.writeFile(archive, bytes, { mode: 0o600 });
    const unpacked = path.join(root, "unpacked");
    await fs.mkdir(unpacked, { recursive: false, mode: 0o700 });
    await (deps.extract ?? extractArchive)(archive, unpacked);
    await fs.rm(archive, { force: true });
    return {
      version: component.version,
      artifactManifestPath: path.join(unpacked, "artifact-v1.json"),
      dispose,
    };
  } catch (error) {
    await dispose();
    throw error;
  }
}
